'use client'

import { useEffect, useState } from 'react'

import type { RouterOutputs } from '@package/api'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@ui/web/components'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

interface BadgeDetailsModalProps {
  className?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Earned achievement to show (from achievement.getAll) */
  achievement: RouterOutputs['achievement']['getAll'][number]
}

export function BadgeDetailsModal({ className, open, onOpenChange, achievement }: BadgeDetailsModalProps) {
  const badge = achievement.achievementBadge
  const showReward = badge.bonusRewardEnabled && badge.bonusXpValue != null && badge.bonusXpValue > 0
  const claimedAt = achievement.claimedAt ?? achievement.createdAt
  // Deleted media leaves a truthy-but-dead iconUrl; fall back to the placeholder on load error.
  const [iconFailed, setIconFailed] = useState(false)
  // Reset when the badge changes — this modal instance is reused across achievements.
  useEffect(() => setIconFailed(false), [badge.iconUrl])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('srd:max-w-sm', className)}>
        <DialogTitle className='srd:sr-only'>Detail Lencana</DialogTitle>
        <DialogDescription className='srd:sr-only'>Detail lencana yang sudah diperoleh</DialogDescription>

        <div className='srd:flex srd:flex-col srd:items-center srd:gap-4 srd:pt-2'>
          {/* Badge icon */}
          <div className='srd:flex srd:items-center srd:justify-center'>
            {badge.iconUrl && !iconFailed ? (
              <img
                src={badge.iconUrl}
                alt=''
                className='srd:size-50 srd:object-contain'
                draggable={false}
                onError={() => setIconFailed(true)}
              />
            ) : (
              <div className='srd:flex srd:size-50 srd:items-center srd:justify-center srd:rounded-full srd:bg-neutrals-n-15 srd:text-4xl srd:font-bold srd:text-neutrals-n-50'>
                ?
              </div>
            )}
          </div>

          {/* Title & description */}
          <div className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:text-center srd:text-complementary-black'>
            <h3 className='srd:text-base srd:font-semibold srd:text-complementary-black srd:line-clamp-2'>
              {badge.title}
            </h3>

            {/* Reward (if any) */}
            {showReward && (
              <p className='srd:text-xs srd:font-bold srd:text-complementary-purple-75'>
                + {badge.bonusXpValue} poin{badge.bonusCoreSkillTag ? ` ${badge.bonusCoreSkillTag.name}` : ''}
              </p>
            )}
          </div>

          {badge.description && (
            <p className='srd:text-xs srd:line-clamp-3 srd:text-complementary-black'>{badge.description}</p>
          )}

          {/* Claimed date (or "Belum diklaim" when unclaimed) */}
          <p className='srd:text-xs srd:text-complementary-black'>
            {achievement.claimedAt != null
              ? formatDate(claimedAt, { dateStyle: 'long', timeStyle: 'short' })
              : 'Belum diklaim'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
